import { RunOfShowItem } from './types';

import ConfirmationPopup from '@/components/core/ConfirmationPopup.vue';
import DialogService from '@/services/dialogs/DialogService';
import NotificationService from '@/services/dialogs/notifications';
import logger from '@/services/logger.service';
import store from '@/store';

interface TimeRange {
  start: number;
  end: number;
}

interface ActiveItems {
  pollId: string;
  publishedPollId: string;
  surveyId: string;
  ticker: { message: string; url?: string } | null;
}

const isItemDeleted = (item: RunOfShowItem) => {
  const currentBroacast = store.getters['event/currentBroadcast'];
  if (item.type === 'POLL') {
    const polls = store.state.event?.avroom?.polls?.details?.questions;
    if (store.state.event.avroom?.channel_loading[currentBroacast?.poll_channel_mmid] === 'loaded') {
      logger.log('polls', polls, item.entity_id);
      return polls && !polls[item.entity_id];
    }
  }
  if (item.type === 'SURVEY') {
    if (!store.state.event.survey?.showLoading) {
      return !store.state.event.survey?.surveys[item.entity_id];
    }
  }
};

export const addOrUpdateRunOfShowItem = (item: RunOfShowItem, previousEntityId?: string) => {
  const currentBroacast = store.getters['event/currentBroadcast'];
  const config = [...(currentBroacast.run_of_show_config || [])].filter(i => !isItemDeleted(i));
  const index = config.findIndex(i => i.entity_id === (previousEntityId || item.entity_id));
  if (index !== -1) {
    config[index] = item;
  } else {
    config.push(item);
  }
  config.sort((a, b) => a.open.start - b.open.start);
  store.dispatch('event/manageCue', {
    broadcastId: currentBroacast.id,
    patch: {
      run_of_show_config: config
    }
  });
};
export const toggleAutomatedRunOfShow = () => {
  const currentBroacast = store.getters['event/currentBroadcast'];
  if (currentBroacast.is_automated_run_of_show) {
    DialogService.show(
      ConfirmationPopup,
      {
        title: '@@translate@@ Disable Automated Run of Show',
        message: '@@translate@@ Are you sure you want to disable Automated Run of Show?',
        confirmText: '@@translate@@ Confirm',
        cancelText: '@@translate@@ Cancel',
        type: 'warning'
      },
      {
        on: {
          onConfirm: () => {
            store.dispatch('event/patchBroadcast', {
              broadcastId: currentBroacast.id,
              patch: {
                is_automated_run_of_show: false
              }
            });
          }
        },
        cancelOnClickOutside: false
      }
    );
  } else {
    enableRunOfShow();
  }
};
export const enableRunOfShow = () => {
  const currentBroacast = store.getters['event/currentBroadcast'];
  const isPollLoaded = store.state.event.avroom?.channel_loading[currentBroacast?.poll_channel_mmid] === 'loaded';
  const isSurveyLoaded = !store.state.event.survey?.showLoading;
  if (!isPollLoaded || !isSurveyLoaded) {
    NotificationService.toast(
      '@@translate@@ Please wait',
      "@@translate@@ Polls and Surveys aren't loaded yet. Please wait for them to load.",
      'warning',
      '',
      5000
    );
    return;
  }
  const currentOpenedPollID = store.state.event.avroom?.polls?.details?.currentOpenedPollID;
  const currentPublishedPollID = store.state.event.avroom?.polls?.details?.currentPublishedPollID;
  const currentOpenSurveyID = store.state.event.survey?.publishedSurveyId;

  DialogService.show(
    ConfirmationPopup,
    {
      title: '@@translate@@ Enable Automated Run of Show',
      message:
        '@@translate@@ Enabling Automated Run of Show will close all open Polls and Surveys. Manual control will be disabled.',
      confirmText: '@@translate@@ Confirm',
      cancelText: '@@translate@@ Cancel',
      type: 'warning'
    },
    {
      on: {
        onConfirm: () => {
          if (currentOpenedPollID) {
            store.dispatch('event/avroom/closeAllOpenedPolls', currentOpenedPollID);
          }
          if (currentPublishedPollID) {
            store.dispatch('event/avroom/unPublishPoll', currentPublishedPollID);
          }
          if (currentOpenSurveyID) {
            store.dispatch('event/survey/unPublishSurvey', currentOpenSurveyID);
          }

          store.dispatch('event/patchBroadcast', {
            broadcastId: currentBroacast.id,
            patch: {
              is_automated_run_of_show: true
            }
          });
        }
      },
      cancelOnClickOutside: false
    }
  );
};

export const deleteRunOfShowItem = (item: RunOfShowItem) => {
  DialogService.show(
    ConfirmationPopup,
    {
      title: item.type === 'POLL' ? '@@translate@@ Delete Poll Cues' : '@@translate@@ Delete Survey Cues',
      message: '@@translate@@ Are you sure you want to delete? This will remove all associated Cues.',
      confirmText: '@@translate@@ Delete Cues',
      cancelText: '@@translate@@ Cancel',
      type: 'warning'
    },
    {
      on: {
        onConfirm: () => {
          const currentBroacast = store.getters['event/currentBroadcast'];
          const config = [...(currentBroacast.run_of_show_config || [])].filter(i => i.entity_id !== item.entity_id);
          store.dispatch('event/manageCue', {
            broadcastId: currentBroacast.id,
            patch: {
              run_of_show_config: config
            }
          });
        }
      },
      cancelOnClickOutside: false
    }
  );
};

export const getErrorsForRunOfShowItem = (item: RunOfShowItem, prevEntityId?: string) => {
  if (item.open.start >= item.open.end) {
    return {
      field: 'open',
      reason: 'Start time should be less than end time'
    };
  }

  if (item.publish && item.publish.start >= item.publish.end) {
    return {
      field: 'publish',
      reason: 'Start time should be less than end time'
    };
  }
  const currentBroacast = store.getters['event/currentBroadcast'];
  const durationOfAllVideoAssets = currentBroacast.durationOfAllVideoAssets / 1000;

  if (item.open.start >= durationOfAllVideoAssets || item.open.end >= durationOfAllVideoAssets) {
    return {
      field: 'open',
      reason: 'Time slot should be within the duration of the video'
    };
  }

  if (item.publish) {
    if (item.publish.start >= durationOfAllVideoAssets || item.publish.end >= durationOfAllVideoAssets) {
      return {
        field: 'publish',
        reason: 'Time slot should be within the duration of the video'
      };
    }
  }

  const config = currentBroacast.run_of_show_config || [];

  for (let i = 0; i < config.length; i++) {
    const exitingItem = config[i];
    if (exitingItem.entity_id === prevEntityId || isItemDeleted(exitingItem)) {
      // Item being updated, ignoring comparisons to it
      continue;
    }

    if (item.type === 'TICKER' && exitingItem.type === 'TICKER') {
      const isStartTimeInBetween = exitingItem.open.start <= item.open.start && exitingItem.open.end >= item.open.start;
      const isEndTimeInBetween = exitingItem.open.start <= item.open.end && exitingItem.open.end >= item.open.end;
      const isCompleteOverlap = item.open.start <= exitingItem.open.start && item.open.end >= exitingItem.open.end;

      if (isStartTimeInBetween || isEndTimeInBetween || isCompleteOverlap) {
        return {
          field: 'open',
          reason: 'Time slot overlaps with another Ticker. Please select a different time slot'
        };
      }
    }

    if (item.type === exitingItem.type && item.type !== 'TICKER') {
      const isStartTimeInBetween = exitingItem.open.start <= item.open.start && exitingItem.open.end >= item.open.start;
      const isEndTimeInBetween = exitingItem.open.start <= item.open.end && exitingItem.open.end >= item.open.end;
      if (isStartTimeInBetween || isEndTimeInBetween) {
        return {
          field: 'open',
          reason: 'Time slot overlaps with another Poll. Please select a different time slot'
        };
      }
    }

    if (item.publish && exitingItem.publish) {
      const isStartTimeInBetween =
        exitingItem.publish.start <= item.publish.start && exitingItem.publish.end >= item.publish.start;
      const isEndTimeInBetween =
        exitingItem.publish.start <= item.publish.end && exitingItem.publish.end >= item.publish.end;
      if (isStartTimeInBetween || isEndTimeInBetween) {
        return {
          field: 'publish',
          reason: 'Time slot overlaps with another Poll. Please select a different time slot'
        };
      }
    }
  }

  return null;
};

export const getTimeString = num => `${Math.floor(num / 60)}:${num % 60 < 10 ? '0' : ''}${Math.floor(num % 60)}`;

function isWithinTimeRange(seekPos: number, range?: TimeRange): boolean {
  if (!range) {
    return false;
  }
  return range.start <= seekPos && range.end >= seekPos;
}

function getActiveItemsAtSeekPosition(runOfShowConfig: RunOfShowItem[], seekPos: number): ActiveItems {
  const activeItems: ActiveItems = {
    pollId: '',
    publishedPollId: '',
    surveyId: '',
    ticker: null
  };

  for (const item of runOfShowConfig) {
    if (isWithinTimeRange(seekPos, item.open)) {
      if (item.type === 'POLL') {
        activeItems.pollId = item.entity_id;
      } else if (item.type === 'SURVEY') {
        activeItems.surveyId = item.entity_id;
      } else if (item.type === 'TICKER') {
        activeItems.ticker = {
          message: item.data?.message || '',
          url: item.data?.url
        };
      }
    }

    if (item.type === 'POLL' && isWithinTimeRange(seekPos, item.publish)) {
      activeItems.publishedPollId = item.entity_id;
    }
  }

  return activeItems;
}

function handlePollStateChange(currentOpenedPollID: string, currentPublishedPollID: string): void {
  const pollDetails = store.state.event.avroom?.polls?.details;
  if (!pollDetails) {
    return;
  }

  if (currentOpenedPollID !== pollDetails.currentOpenedPollID) {
    pollDetails.currentOpenedPollID = currentOpenedPollID;
    if (currentOpenedPollID) {
      store.dispatch('event/avroom/showNewPollNotification');
    }
  }

  if (currentPublishedPollID !== pollDetails.currentPublishedPollID) {
    pollDetails.currentPublishedPollID = currentPublishedPollID;
  }
}

function handleSurveyStateChange(currentOpenSurveyID: string, alreadyOpenSurvey: string | null | undefined): void {
  if (!store.state.event.survey || currentOpenSurveyID === alreadyOpenSurvey) {
    return;
  }

  if (currentOpenSurveyID) {
    store.commit('event/survey/setPublishedSurveyId', currentOpenSurveyID);
    store.dispatch('event/survey/openSurvey', currentOpenSurveyID);
    return;
  }

  if (!alreadyOpenSurvey) {
    return;
  }

  const surveyStatus = store.state.event.survey?.surveys[alreadyOpenSurvey]?.surveyStatus;
  if (surveyStatus !== 'CLOSING') {
    const time = store.getters['event/currentBroadcast'].is_live ? '30' : '5';
    store.dispatch('event/survey/closeSurveyAndStartTimer', {
      surveyId: alreadyOpenSurvey,
      time
    });
  }
}

function isShownTickerFromScheduledItemThatEnded(
  runOfShowConfig: RunOfShowItem[],
  seekPos: number,
  tickerText: string,
  tickerUrl: string | null
): boolean {
  const normalizedUrl = tickerUrl ?? '';
  return runOfShowConfig.some(
    item =>
      item.type === 'TICKER' &&
      seekPos > item.open.end &&
      (item.data?.message || '') === tickerText &&
      (item.data?.url ?? '') === normalizedUrl
  );
}

function handleTickerStateChange(
  currentOpenTicker: { message: string; url?: string } | null,
  currentBroadcast: { id: string; show_ticker: boolean; ticker_text: string; ticker_url: string },
  runOfShowConfig: RunOfShowItem[],
  seekPos: number
): void {
  const currentTickerMessage = currentOpenTicker?.message || '';
  const currentTickerUrl = currentOpenTicker?.url || null;

  const isTickerActive =
    currentBroadcast.show_ticker &&
    currentBroadcast.ticker_text === currentTickerMessage &&
    currentBroadcast.ticker_url === currentTickerUrl;

  if (currentOpenTicker && currentTickerMessage) {
    if (!isTickerActive) {
      store.dispatch('event/shareTicker', {
        tickerText: currentTickerMessage,
        tickerUrl: currentTickerUrl,
        broadcastId: currentBroadcast.id
      });
    }
  } else if (
    currentBroadcast.show_ticker &&
    isShownTickerFromScheduledItemThatEnded(
      runOfShowConfig,
      seekPos,
      currentBroadcast.ticker_text,
      currentBroadcast.ticker_url ?? null
    )
  ) {
    store.dispatch('event/stopSharingTicker', currentBroadcast.id);
  }
}

export function handleRunOfShow(seekPos: number): void {
  const currentBroadcast = store.getters['event/currentBroadcast'];
  if (!currentBroadcast) {
    return;
  }

  const alreadyOpenSurvey = store.state.event.survey?.publishedSurveyId;
  const runOfShowConfig: RunOfShowItem[] = currentBroadcast.run_of_show_config || [];

  const activeItems = getActiveItemsAtSeekPosition(runOfShowConfig, seekPos);

  handlePollStateChange(activeItems.pollId, activeItems.publishedPollId);
  handleSurveyStateChange(activeItems.surveyId, alreadyOpenSurvey);
  handleTickerStateChange(activeItems.ticker, currentBroadcast, runOfShowConfig, seekPos);
}
